export const CHECKOUT_URL = "https://abdo-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/j64PebyUUmFjE6iHhaxsek/ABDO-EN.png",
  doctor: "https://cdn.magicpatterns.com/uploads/fcoQ4Cge4MJKDh6zeCCPWA/MICHAEL_CARTER_-_IMPLANTES.png",
};

export const frameworkSteps = [
  { title: "Observe", question: "What do you actually see in the case?" },
  { title: "Organize", question: "How can you structure the information in front of you?" },
  { title: "Identify", question: "Which variables appear relevant and deserve further review?" },
  { title: "Prioritize", question: "What deserves your attention first?" },
  { title: "Review", question: "What information, evidence or educational resources should you investigate?" },
  { title: "Reflect", question: "What did you learn from the case?" },
];

export const painPoints = [
  {
    icon: "compass",
    title: "You look at a new abdominoplasty case and don't know what to analyze first.",
    body: "There are multiple aesthetic, anatomical, patient-specific and documentation variables competing for your attention.",
  },
  {
    icon: "clock",
    title: "You spend too much time jumping between information.",
    body: "Before reviewing a case, you move between articles, lectures, notes, photographs and previous cases.",
  },
  {
    icon: "shuffle",
    title: "Every case seems completely different.",
    body: "You have experience, but you don't always have a consistent framework for comparing one educational case with another.",
  },
  {
    icon: "book",
    title: "You consume information but don't get enough opportunities to practice your reasoning.",
    body: "Another lecture gives you more information. It doesn't necessarily give you another opportunity to analyze a case.",
  },
  {
    icon: "brain",
    title: "You want to become better at identifying what matters.",
    body: "Not because you need someone to make decisions for you — but because you want a more disciplined process for organizing what you're seeing.",
  },
  {
    icon: "notebook",
    title: "You finish a case without systematically capturing what you learned.",
    body: "You move to the next case and eventually forget what you noticed, what you missed and what you wanted to review.",
  },
];

export type Volume = {
  number: string;
  title: string;
  lead: string;
  body?: string;
  flow?: string[];
  listLabel?: string;
  items?: string[];
  steps?: string[];
  closing?: string;
  featured?: boolean;
};

export const volumes: Volume[] = [
  {
    number: "I",
    title: "The 15-Minute Abdominoplasty Case Analysis Framework™",
    lead: "Stop staring at a case wondering where to begin.",
    body: "I'll walk you through a structured framework for breaking an educational abdominoplasty case into focused stages:",
    flow: ["Case", "Observation", "Variables", "Questions", "Priorities", "Review", "Reflection"],
    closing: "You'll also receive the 15-Minute Case Analysis Sheet™ so you can repeat the process again and again.",
  },
  {
    number: "II",
    title: "The Abdominoplasty Analysis Map™",
    lead: "Learn to look at the case as a complete picture instead of focusing on isolated details.",
    listLabel: "Organize your educational case review around:",
    items: [
      "Patient profile",
      "Aesthetic objectives",
      "Relevant observations",
      "Abdominal contour",
      "Visual proportions",
      "Case documentation",
      "Areas requiring additional review",
      "Questions to investigate",
      "Learning points",
    ],
    closing: "The goal is simple: turn a complex visual case into an organized analysis.",
  },
  {
    number: "III",
    title: "The Abdominoplasty Case Planning Workbook™",
    lead: "Turn your observations into a structured educational case review.",
    listLabel: "You'll work through:",
    items: [
      "Case summary",
      "Patient objectives",
      "Key observations",
      "Areas requiring attention",
      "Questions to investigate",
      "Priorities",
      "Documentation prompts",
      "Review prompts",
      "Educational planning notes",
      "Post-case reflection",
      "Lessons learned",
    ],
    closing:
      "This is not a universal surgical recipe. It's a structured place to practice your professional thinking around educational cases.",
  },
  {
    number: "IV",
    title: "The Abdominoplasty Case Atlas™",
    lead: "Instead of simply reading theory, you'll practice analyzing educational cases.",
    listLabel: "Each case follows the same process:",
    steps: [
      "Review the case.",
      "Write down what you notice.",
      "Identify the variables you believe deserve attention.",
      "Document your questions.",
      "Prioritize what you would review.",
      "Compare your reasoning with the educational review.",
      "Record what you learned.",
    ],
    closing: "This transforms passive consumption into active case practice.",
    featured: true,
  },
  {
    number: "V",
    title: "The Abdominoplasty Complication & Decision Review Atlas™",
    lead: "Study difficult educational scenarios before encountering them in your learning process.",
    listLabel: "Each scenario is organized around questions such as:",
    items: [
      "What happened?",
      "What information should be reviewed?",
      "Which factors deserve additional attention?",
      "What questions would you ask?",
      "What information was missing?",
      "What could have been documented better?",
      "What would you review differently?",
      "What educational lesson can be extracted?",
    ],
    closing:
      "The objective isn't to memorize answers. It's to train yourself to notice what deserves closer examination.",
  },
  {
    number: "VI",
    title: "The Surgeon's Abdominoplasty Case Review System™",
    lead: "Turn every educational case into another opportunity to learn.",
    listLabel: "You'll receive reusable templates for:",
    items: [
      "Case documentation",
      "Case reflection",
      "Case comparison",
      "Learning points",
      "Questions for future review",
      "Personal knowledge gaps",
      "Monthly case review",
      "Progress tracking",
      "Annual professional reflection",
    ],
    closing:
      "Instead of simply completing another case, you'll gradually build your own professional learning archive.",
  },
];

export const tools = [
  {
    icon: "sheet",
    title: "The 15-Minute Case Analysis Sheet™",
    body: "A structured worksheet for practicing the complete case-review framework.",
  },
  {
    icon: "checklist",
    title: "The Pre-Case Review Checklist™",
    body: "Organize the information you want to examine before reviewing an educational case.",
  },
  {
    icon: "map",
    title: "The Abdominoplasty Analysis Map™",
    body: "A visual framework for organizing observations across the case.",
  },
  {
    icon: "matrix",
    title: "The Case Comparison Matrix™",
    body: "Compare educational cases side by side and identify similarities, differences and learning points.",
  },
  {
    icon: "camera",
    title: "The Visual Case Documentation Guide™",
    body: "A structured educational framework for organizing photographic and visual case documentation.",
  },
];

export type Bonus = {
  number: number;
  title: string;
  value: string;
  image: string;
  body: string;
  listLabel?: string;
  items?: string[];
  tagline: string;
};

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: "30 Educational Abdominoplasty Cases",
    value: "$47",
    image: "https://cdn.magicpatterns.com/uploads/jZtLNprvPCUnAxkEjaAJqt/BONUS_1_ABDO-EN.png",
    body: "A library of additional educational case exercises designed to help you practice the framework repeatedly. Instead of reading another chapter, you'll have another case to analyze.",
    tagline: "Case → Think → Document → Review → Learn.",
  },
  {
    number: 2,
    title: "The 10-Minute Abdominoplasty Case Review™",
    value: "$37",
    image: "https://cdn.magicpatterns.com/uploads/4mHL2h3QLSZBjC8Jpe2ftB/BONUS_2_ABDO-EN.png",
    body: "A compact version of the Master System designed for shorter educational case reviews. When you don't have time for the full framework:",
    tagline: "10 minutes. One case. One structured review.",
  },
  {
    number: 3,
    title: "The Abdominoplasty Error Atlas™",
    value: "$47",
    image: "https://cdn.magicpatterns.com/uploads/bstau2XrMUL9rAWmS4bpvM/BONUS_3_ABDO-EN.png",
    body: "Educational scenarios focused on common case-analysis mistakes and missed considerations.",
    listLabel: "For each scenario:",
    items: [
      "What happened?",
      "What may have been overlooked?",
      "What questions deserve attention?",
      "What should be reviewed?",
      "What lesson can be extracted?",
    ],
    tagline: "Don't simply memorize mistakes. Train yourself to look for them.",
  },
  {
    number: 4,
    title: "The Surgeon's Case Review Cards™",
    value: "$37",
    image: "https://cdn.magicpatterns.com/uploads/6md8cbeY1mJrndPnPbayAf/BONUS_4_ABDO-EN.png",
    body: "Printable case-review prompts designed for:",
    items: ["Individual study", "Peer discussion", "Case conferences", "Resident education", "Self-reflection"],
    tagline: "One card. One question. One opportunity to sharpen your reasoning.",
  },
  {
    number: 5,
    title: "The 30-Day Abdominoplasty Case Analysis Challenge™",
    value: "$29",
    image: "https://cdn.magicpatterns.com/uploads/73Q6B55n8pkSDAgWps6BwY/BONUS_5_ABDO-EN.png",
    body: "A 30-day professional practice calendar designed to make structured case analysis a consistent learning habit. Every day:",
    tagline: "One case. One exercise. One reflection. One lesson.",
  },
];

export const guaranteeItems = [
  "Open all six volumes",
  "Review the case-analysis framework",
  "Work through the educational cases",
  "Download the professional tools",
  "Explore all five bonuses",
  "Print the worksheets",
  "Test the 15-minute framework",
];

export const beforeItems = [
  "You consume information passively.",
  "Every case feels like a completely new problem.",
  "Your case notes are inconsistent.",
  "You jump between different resources before reviewing a case.",
  "You don't have a repeatable process for organizing your observations.",
  "You finish a case without systematically extracting the lesson.",
];

export const afterItems = [
  "You have a structured case-review framework.",
  "You can work through an educational case in approximately 15 minutes.",
  "You have standardized worksheets for your case reviews.",
  "You can compare cases using the same framework.",
  "You can identify questions and variables that deserve further review.",
  "You can document what you learned from each case.",
];

export const testimonials = [
  {
    name: "Dr. Sarah Jenkins",
    role: "Plastic Surgeon · Denver, USA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/e78fc24d-637d-4f42-9979-54c049b3ee07.jpg",
    quote: "I want to know where to start.",
    answer: "The 15-Minute Case Analysis Framework™ gives you a repeatable starting point.",
  },
  {
    name: "Dr. Marcus Bell",
    role: "Aesthetic Physician · Atlanta, USA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/7c3f3680-7794-44de-9da5-519ff474c3af.jpg",
    quote: "I want to stop reviewing cases randomly.",
    answer: "The Analysis Map™ gives you a consistent structure.",
  },
  {
    name: "Dr. Elena Ruiz",
    role: "Plastic Surgery Resident · Miami, USA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/d53f14cb-c18c-44e0-a0ef-04fca27a2066.jpg",
    quote: "I want more opportunities to practice.",
    answer: "The Case Atlas™ and 30 Educational Cases give you repeated case exercises.",
  },
  {
    name: "Dr. Arjun Mehta",
    role: "Plastic Surgeon · London, UK",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/5379acb3-7860-4b5d-a516-54f0ca27112b.jpg",
    quote: "I want to remember what I learned.",
    answer: "The Case Review System™ turns individual cases into an ongoing learning archive.",
  },
  {
    name: "Dr. Grace Lin",
    role: "Aesthetic Physician · Toronto, Canada",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/2a3a9e8c-aec3-45ae-a354-115438185614.jpg",
    quote: "I want something I can actually use.",
    answer: "Every major framework comes with printable tools and worksheets.",
  },
];

export const faqs = [
  {
    q: "How will I receive The Abdominoplasty Master System™?",
    a: "Immediately after your purchase, you'll receive access to the digital materials and downloadable resources included in the product.",
  },
  {
    q: "Who is The Abdominoplasty Master System™ for?",
    a: "It is designed for qualified plastic surgeons, aesthetic physicians and other appropriately trained healthcare professionals interested in case-based educational review of abdominoplasty cases.",
  },
  {
    q: "Is this a surgical technique course?",
    a: "No. The Master System is designed as a case-based educational and professional reasoning framework. It does not replace formal surgical training, supervised experience, current evidence, institutional protocols or individualized clinical judgment.",
  },
  {
    q: "Does the system provide a universal surgical protocol?",
    a: "No. The purpose is to help you structure educational case analysis, organize observations, identify questions and reflect on cases.",
  },
  {
    q: "How long does each case take?",
    a: "The core framework is designed around a 15-minute educational case review. You can also use the shorter 10-Minute Case Review™ included as Bonus #2.",
  },
  {
    q: "Can I print the materials?",
    a: "Yes. The worksheets, boards, checklists and case-review tools are designed to be downloadable and printable.",
  },
  {
    q: "Is this a one-time payment?",
    a: "Yes. The launch price is $27 USD as a one-time payment. There are no monthly subscription fees.",
  },
  {
    q: "How long will I have access?",
    a: "You receive lifetime digital access to the materials included with your purchase, subject to the applicable purchase terms.",
  },
  {
    q: "What happens if I don't like it?",
    a: "You have a 7-day guarantee. Review the system and decide whether it provides value for your professional learning. If it isn't right for you, contact us within the guarantee period according to the purchase terms.",
  },
];

export const recentBuyers = [
  { name: "Dr. Alejandro Torres", country: "Mexico" },
  { name: "Dr. Valentina Gómez", country: "Colombia" },
  { name: "Dr. Martín Fernández", country: "Argentina" },
  { name: "Dr. Camila Rojas", country: "Chile" },
  { name: "Dr. Javier Morales", country: "Spain" },
  { name: "Dr. Lucía Ramírez", country: "Peru" },
  { name: "Dr. Andrés Castillo", country: "Ecuador" },
  { name: "Dr. Sofía Herrera", country: "Costa Rica" },
  { name: "Dr. Diego Vargas", country: "Uruguay" },
  { name: "Dr. Isabel Navarro", country: "Dominican Republic" },
  { name: "Dr. Ricardo Mendoza", country: "Panama" },
  { name: "Dr. Gabriela Pérez", country: "Guatemala" },
  { name: "Dr. Fernando Ortiz", country: "Paraguay" },
  { name: "Dr. Mariana Silva", country: "Puerto Rico" },
  { name: "Dr. Carlos Medina", country: "Bolivia" },
];
